import { Link } from '@tanstack/react-router'
import { ArrowRight, ShieldCheck } from 'lucide-react'

import { ErrorSurface } from '@/components/error-surface'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { useApprovalInbox } from '@/features/approvals/queries'

/**
 * How much dual-control work is waiting on this person, right now.
 *
 * Not gated on a permission here, the same way the "Approvals" navigation entry never has been:
 * `GET /access/approvals/inbox` already answers only with what this reader may decide on, so a
 * role holding no countersigning permission at all sees an honest, permanently empty inbox rather
 * than a tile hidden for a reason nobody explains. See `approvals-api.ts`'s own note on the 500-row
 * server-side cap this count inherits.
 */
export function ApprovalsInboxTile() {
  const query = useApprovalInbox()

  return (
    <Card className="flex flex-col">
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <ShieldCheck className="size-4 text-muted" aria-hidden />
          Approvals
        </CardTitle>
      </CardHeader>

      <CardContent className="flex flex-1 flex-col justify-between gap-3">
        {query.isPending ? (
          <Skeleton className="h-14 w-full" />
        ) : query.isError ? (
          <ErrorSurface
            error={query.error}
            onRetry={() => void query.refetch()}
            title="The inbox could not be read"
          />
        ) : query.data.length === 0 ? (
          <p className="text-sm text-muted">Nothing is waiting on you.</p>
        ) : (
          <p className="numeric text-2xl font-semibold tabular-nums text-heading">
            {query.data.length}
            <span className="ml-1.5 text-sm font-normal text-muted">
              {query.data.length === 1 ? 'request waiting on you' : 'requests waiting on you'}
            </span>
          </p>
        )}

        <Button asChild variant="secondary" size="sm" className="self-start">
          <Link to="/approvals">
            Go to approvals
            <ArrowRight />
          </Link>
        </Button>
      </CardContent>
    </Card>
  )
}
